import type { ConversationMessage } from '@/domains/jobs/types/workOrder';
import { resolveAssigneeLabel } from '@/shared/lib/assignee-utils';
import {
  type JobPermit,
  type PermitCategory,
  type Psr,
  isVulnerablePsr,
} from '@skewb-works/contracts';
import { useJobConversations } from './useJobConversations';
import { useJobDetail } from './useJobDetail';

export interface JobData {
  id: string;
  title: string;
  description: string;
  client: string;
  siteAddress: string;
  address: string;
  /**
   * Display name of the job's priority, normalised to 'Unknown' when the job
   * has none. Never defaulted to a real priority — showing 'Medium' (or the
   * old 'medium') for an unset priority is what SC-6369 was raised about.
   */
  priority: string;
  /** `job_priority.id`, for the editable priority selector. */
  priorityId?: number | null;
  /** Raw lookup name as stored, null when the job has no priority row. */
  priorityName?: string | null;
  status: string;
  assignee?: string;
  scheduledDate?: string;
  /**
   * True when a live schedule row exists for the job. Distinct from
   * `scheduledDate`/`assignee`, which are display strings and can be absent even
   * on a scheduled job (schedule.start_date_time and team_id are both nullable).
   * Gates the "won't show on mobile" warning (WRK-5792).
   */
  isScheduled?: boolean;
  dueDate?: string;
  requiredBy?: string;
  createdDate?: string;
  estimatedHours?: number;
  tags?: string[];
  category?: string;
  milestones?: Record<string, boolean>;
  tasks?: Array<{
    id: number;
    name: string;
    // Raw job_task.status id (JOB_TASK scheme); see TasksCard for id -> label.
    status: number | null;
    orderNumber?: number;
    isCategoryTask?: boolean;
    isMandatory?: boolean;
    isMandatoryOverridden?: boolean;
    // True when the job category makes this task mandatory — it cannot be
    // relaxed per job, only in Settings -> Task Types.
    isMandatoryLocked?: boolean;
  }>;
  formResponses?: unknown;
  capturedImages?: unknown;
  notes?: unknown;
  validated?: boolean;
  hasResponse?: boolean;
  responseDate?: string;
  responseMessage?: string;
  hasBeenResent?: boolean;
  resentDate?: string;
  resentInformation?: string;
  duration?: number;
  coordinates?: string;
  contractName?: string;
  externalId?: string | null;
  applicationReference?: string | null;
  mirPendingDirection?: 'inbound' | 'outbound' | null;
  mirConversationHistory?: ConversationMessage[];
  name?: string | null;
  jobStatusName?: string | null;
  permitRequired?: boolean;
  reinstatementRequired?: boolean;
  permitStatusId?: number | null;
  permitStatusName?: string | null;
  psr?: Psr | null;
  isVulnerable?: boolean;
  orderType?: string | null;
  permit?: JobPermit | null;
  permitRequestSentAt?: string | null;
  permitCategory?: PermitCategory | null;
  /**
   * True when the job has a mandatory task not yet resolved, or Completed with
   * no submitted form (findOutstandingMandatoryWork.length > 0 server-side).
   * Drives the WRK-9999 override on the technical-completion permit gate — see
   * getTechnicalCompletionGate in StatusBar.tsx.
   */
  mandatoryWorkOutstanding?: boolean;
  /**
   * True once the engineer has reached site — the work order is at 'On Site' or
   * beyond (hasReachedOnSite server-side). Gates the permit actions in
   * PermitCard. Prefer this over comparing `status` to 'On Site': the work order
   * auto-promotes to 'Engineering Completed' when the last mandatory form is
   * submitted, and the engineer is still on site at that point (CWL-118).
   */
  engineerOnSite?: boolean;
}

export function useJobData(jobId: string) {
  const numericId = Number(jobId);
  const { data: apiData, isLoading, isError } = useJobDetail(numericId);
  const { data: conversationsData } = useJobConversations(numericId);

  const conversationThread: ConversationMessage[] = (conversationsData ?? []).map((msg) => ({
    id: String(msg.id),
    type: msg.type === 'accepted' || msg.type === 'other' ? 'resent' : msg.type,
    sender: msg.sender,
    timestamp: msg.timestamp,
    message: msg.message,
    createdBy: msg.createdBy,
    eventType: msg.eventType,
    files: msg.attachments?.map((a) => ({
      name: a.fileName,
      s3Key: a.S3Key,
      mimeType: a.mimeType,
    })),
  }));

  let jobData: JobData;
  let jobStatus:
    | 'new'
    | 'more-info-requested'
    | 'rejected'
    | 'accepted'
    | 'completed'
    | 'validated'
    | null = null;

  const mirConversationHistory = conversationThread
    .filter((m) => m.type === 'request-info' || m.type === 'response')
    .sort((a, b) => b.timestamp.localeCompare(a.timestamp));

  if (apiData) {
    const statusName = apiData.work_order_status?.toLowerCase() ?? '';

    if (statusName === 'validated') jobStatus = 'validated';
    else if (statusName === 'completed' || statusName === 'complete') jobStatus = 'completed';
    else if (statusName === 'accepted' || statusName === 'in progress') jobStatus = 'accepted';
    else if (statusName === 'rejected') jobStatus = 'rejected';
    else if (statusName === 'more info requested' || statusName === 'more-info-requested')
      jobStatus = 'more-info-requested';
    else jobStatus = 'new';

    jobData = {
      id: String(apiData.id),
      title: apiData.name ?? 'Untitled Job',
      description: apiData.description ?? '',
      client: apiData.client_name ?? 'Unknown Client',
      siteAddress: apiData.address ?? '',
      address: [apiData.address, apiData.town, apiData.pincode].filter(Boolean).join(', '),
      // The legacy free-text `priority` column is deliberately not a fallback:
      // it holds whatever CORE sent, interpreted against a different id list.
      // An absent priority is 'Unknown', not 'medium' (SC-6369).
      priority: apiData.priority_name ?? 'Unknown',
      priorityId: apiData.priority_id ?? null,
      priorityName: apiData.priority_name ?? null,
      status: apiData.work_order_status ?? 'unknown',
      assignee: resolveAssigneeLabel(apiData.team_name, apiData.assigned_to_name),
      // Only the schedule row carries a real scheduled datetime; job.start_date
      // is set by CORE/SAP on every work order regardless of scheduling and must
      // not surface as "Scheduled" (WRK-5663).
      scheduledDate: apiData.scheduled_date_time ?? undefined,
      isScheduled: apiData.is_scheduled,
      dueDate: apiData.req_completion_date ?? undefined,
      requiredBy: apiData.req_completion_date ?? undefined,
      createdDate: apiData.created_time ?? undefined,
      tags: apiData.tags ? (apiData.tags as string[]) : undefined,
      category: apiData.job_category_name ?? undefined,
      milestones: apiData.milestones,
      tasks: apiData.tasks?.map((t) => ({
        id: t.id,
        name: t.name ?? 'Unnamed Task',
        status: t.status ?? null,
        orderNumber: t.orderNumber ?? undefined,
        isCategoryTask: t.isCategoryTask ?? false,
        // Mandatory state must be passed through, not defaulted here — dropping
        // these pinned the Tasks card checkbox to its `?? true` fallback so it
        // never reflected or cleared the real value.
        isMandatory: t.isMandatory ?? true,
        isMandatoryOverridden: t.isMandatoryOverridden ?? false,
        isMandatoryLocked: t.isMandatoryLocked ?? false,
      })),
      validated: apiData.validated,
      duration: apiData.duration ?? undefined,
      coordinates: apiData.coordinates ?? undefined,
      contractName: apiData.contract_name ?? undefined,
      notes: apiData.notes ?? undefined,
      externalId: apiData.external_id ?? null,
      applicationReference: apiData.application_reference ?? null,
      mirPendingDirection: apiData.mir_pending_direction ?? null,
      mirConversationHistory,
      name: apiData.name ?? null,
      jobStatusName: apiData.job_status_name ?? null,
      permitRequired: apiData.permit_required,
      reinstatementRequired: apiData.reinstatement_required,
      permitStatusId: apiData.permit_status_id ?? null,
      permitStatusName: apiData.permit_status_name ?? null,
      psr: apiData.psr ?? null,
      isVulnerable: isVulnerablePsr(apiData.psr),
      orderType: apiData.order_type ?? null,
      permit: apiData.permit ?? null,
      permitRequestSentAt: apiData.permit_request_sent_at ?? null,
      permitCategory: apiData.permit_category ?? null,
      mandatoryWorkOutstanding: apiData.mandatory_work_outstanding,
      engineerOnSite: apiData.engineer_on_site,
      hasResponse: conversationThread.some((m) => m.type === 'response'),
    };
  } else {
    jobData = {
      // WRK-5897: never echo the raw route param back into the UI — an
      // invalid jobId here could be arbitrary attacker-controlled text
      // (content injection/spoofing), not necessarily a real job id.
      id: Number.isFinite(numericId) ? String(numericId) : '—',
      title: isLoading ? 'Loading...' : 'Job Not Found',
      description: isLoading ? '' : 'This job could not be found in the system.',
      client: 'Unknown',
      siteAddress: 'Unknown',
      address: 'Unknown',
      priority: 'Unknown',
      priorityId: null,
      priorityName: null,
      status: 'pending',
    };
  }

  return { jobData, jobStatus, conversationThread, isLoading, isError };
}
